export enum MarketplaceItemStatus {
  Active = 'activated',
  Draft = 'draft',
  Unpublished = 'unpublished',
  Scheduled = 'scheduled',
}

// use to change filter of select offer, to show only offers with the same acquisition type
// used to map v2/offers endpoint values to acquisition types on form values
export enum AcquisitionType {
  Subscription = 'assinatura',
  Purchase = 'compra',
  Rental = 'aluguel',
}

export const ACQUISITION_LABELS: Record<AcquisitionType, string> = {
  [AcquisitionType.Purchase]: 'Compra',
  [AcquisitionType.Subscription]: 'Assinatura',
  [AcquisitionType.Rental]: 'Aluguel',
};

export type TargetMarket = 'subscriber' | 'freemium';

// use to change filter of select offer, to show only offers with the same target market
// used on v2/offers endpoint values to map target market on form values
export const AUDIENCE_TARGET_MAP: Record<TargetMarket, string> = {
  subscriber: 'marketplace-assinante',
  freemium: 'marketplace-freemium',
};

export enum ProductType {
  Program = 'program',
  Combo = 'combo',
}

export enum CopyType {
  Product = 'product',
  UserAccess = 'user_access',
  Reviews = 'reviews',
  Instructor = 'instructor',
}

export enum BannerType {
  Home = 'home',
  BannerTop = 'banner-top',
  BannerMiddle = 'banner-middle',
  BannerBottom = 'banner-bottom',
}

export interface MarketplaceCategory {
  id: number;
  name: string;
  color: string;
}

export interface MarketplaceLane {
  id: number;
  name: string;
  type: string;
}

export interface MarketplaceOffer {
  basePaymentOfferId: string;
  tempPaymentOfferId?: string;
  targetMarket: TargetMarket;
  tag?: string;
  payOffer?: MarketplaceOfferV2;
}

export interface MarketplaceOfferGroup {
  id?: number;
  title: string;
  subtitle?: string;
  offers: MarketplaceOffer[];
}

export interface Review {
  author: string;
  rating: number;
  comment: string;
}

export interface InstructorInfo {
  name: string;
  cref?: string;
  image?: string;
}

export interface LandingPageCopySection {
  type: CopyType;
  title?: string;
  items?: string[];
  reviews?: Review[];
  instructor?: InstructorInfo;
  paragraphs?: string[];
}

export interface LandingPageData {
  title?: string;
  durationTime?: string;
  programs?: {
    length?: string;
  };
  level?: string;
  objective?: string;
  description?: string;
  buttonText?: string;
  teaser?: string;
  imageTeaser?: string;
  copy?: LandingPageCopySection[];
}

export interface ProductDetails {
  buttonText?: string;
  landingPageData?: LandingPageData;
}

export interface MarketplaceImages {
  program?: {
    card?: string;
    logo?: string;
    vertical?: string;
  };
  combo?: {
    main?: string;
    search?: string;
    highlight?: {
      original?: string;
      small?: string;
    };
  };
  landingPage?: {
    teaser?: string | null;
    program?: {
      instructor?: string | null;
    };
    combo?: {
      whatYouGet?: string[];
    };
  };
}

export interface MarketplaceProduct {
  id?: number;
  title: string;
  description?: string;
  type: ProductType;
  releaseDate?: string | null;
  status?: MarketplaceItemStatus;
  category: MarketplaceCategory;
  lanes: MarketplaceLane[];
  productDetails?: ProductDetails;
  offerGroups?: MarketplaceOfferGroup[];
  images?: MarketplaceImages;
}

export type MarketplacePayload = Pick<MarketplaceProduct, 'title' | 'type'> &
  Omit<Partial<MarketplaceProduct>, 'title' | 'type'>;

export type MarketplaceProductResponse = MarketplaceProduct & { id: number };
export interface PlanTemporality {
  checkoutId: string;
  offerId: string;
  listPrice: number;
  salePrice: number;
  type: string;
  typeName: string;
  listFullPrice?: number;
  saleFullPrice?: number;
  installments?: number;
  info?: string;
  tag?: string;
}

export interface MarketplaceOfferProperty {
  id: number;
  name?: string;
  key?: string;
  value?: string;
  slug?: string;
}

export interface MarketplaceOffersParams {
  limit?: number;
  page?: number;
  order?: string;
  filter?: string;
  status?: string[];
  acquisitionId?: number;
  typeId?: number;
  seasonalityId?: number;
  label?: string[];
  initDate?: string;
  endDate?: string;
  search?: string;
}

export interface MarketplaceOfferV2 {
  id: number;
  name?: string;
  offerId?: string;
  checkoutId?: string;
  fullPrice?: number;
  listPrice?: number;
  salePrice?: number;
  tag?: string;
  status?: string;
  acquisitionId?: number;
  typeId?: number;
  seasonalityId?: number;
  label?: string;
  isAvailableMobile?: boolean;
  acquisition?: MarketplaceOfferProperty;
  type?: MarketplaceOfferProperty;
  seasonality?: MarketplaceOfferProperty;
  [key: string]: unknown;
}

export interface MarketplaceOffersListResponse {
  data: MarketplaceOfferV2[];
  total: number;
}

export interface MarketplaceOfferSelectData {
  id: number;
  offerId: string;
  checkoutId: string;
  name: string;
  fullPrice: number;
  listPrice: number;
  salePrice: number;
  type: string;
  typeName?: string;
  seasonalityName?: string;
  acquisitionName?: string;
  tag?: string;
}

export interface MarketplacePropertyItem {
  id: number;
  name: string;
  code: string;
  description?: string | null;
}

export interface MarketplaceAcquisitionTypeItem
  extends MarketplacePropertyItem {
  renewalDisabled: boolean;
  onlyFull: boolean;
}

export interface MarketplaceSeasonalityItem {
  id: number;
  name: string;
  period: number;
  installments: number;
  warrantyDays: number;
  description?: string | null;
}

export interface MarketplacePropertiesResponse {
  types: MarketplacePropertyItem[];
  acquisitionTypes: MarketplaceAcquisitionTypeItem[];
  seasonalities: MarketplaceSeasonalityItem[];
}

export interface Banner {
  id: number;
  imageWeb: string;
  imageMobile: string;
  imageTablet?: string;
  linkWeb: string;
  linkMobile: string;
  type: BannerType;
}

export interface MarketplaceItem {
  id: number;
  title: string;
  description?: string;
  type: ProductType;
  releaseDate: string | null;
  status: MarketplaceItemStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface MarketplaceListParams {
  type: ProductType;
  page?: number;
  limit?: number;
  title?: string;
  status?: MarketplaceItemStatus;
  startDate?: string;
  endDate?: string;
}

export interface MarketplaceListResponse {
  data: MarketplaceItem[];
  total: number;
}

export interface MarketplaceCategory {
  id: number;
  name: string;
  color: string;
}
export interface MarketplaceLane {
  id: number;
  name: string;
  type: string;
}
